import type {
	BackupCodeSchema,
	CookieAuth,
	GAuthSchema,
	IMAPSchema,
	OrderUnifiedOutput,
	getOneOrderOutput,
} from "@/api/custom_types";
import i18next from "i18next";
import { throwOnDev } from "./miscUtils";
import type { TranslationF } from "./type-utils";

export const formatCurrency = (value: number, separator = ","): string => {
	return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

export const formatNumber = (
	value: number | null | undefined | string,
	dataType:
		| "percent"
		| "price"
		| "large_count"
		| "count"
		| "thousands" = "count",
): string => {
	let formattedValue: number;
	formattedValue = value ? +value : 0;

	function formatDecimalIfNeeded(num: number): string {
		const numStr = num.toLocaleString();
		const dotIndex = numStr.indexOf(".");
		if (dotIndex !== -1 && numStr.length - dotIndex - 1 > 2) {
			return Number.parseFloat(num.toFixed(2)).toLocaleString();
		}
		return numStr;
	}

	switch (dataType) {
		case "percent":
			return `${formatDecimalIfNeeded(formattedValue)}%`;

		case "count":
			return formattedValue.toLocaleString();

		case "large_count": {
			const valueInMillions = formattedValue / 1_000_000;
			return `${formatDecimalIfNeeded(valueInMillions)}${i18next.t(
				"signs.millions",
			)}`;
		}

		case "price":
			return `${formattedValue.toLocaleString()}${i18next.t("signs.euro")}`;

		case "thousands":
			return `${formatDecimalIfNeeded(formattedValue / 1_000)}${i18next.t(
				"signs.thousands",
			)}`;

		default:
			dataType satisfies never;
			throwOnDev(`Unknown dataType: ${dataType}`);
	}
};

export function calculateTaxPercentage(
	toTransfer: number,
	taken: number,
): number {
	if (taken <= 0) {
		return 0;
	}

	const value = (1 - toTransfer / taken) * 100;
	return Number((Math.ceil(value * 100) / 100).toFixed(2));
}

/** The order's EA email, or the "no account yet" label for a customer-link order not claimed yet */
export const getOrderEmail = (
	order: OrderUnifiedOutput | getOneOrderOutput,
): string =>
	"ea_email" in order ? order.ea_email : i18next.t("order.noAccountYet");

export const renderAuthData = (
	data:
		| (GAuthSchema | IMAPSchema | BackupCodeSchema | CookieAuth)
		| null
		| undefined,
) => {
	if (!data) return "";

	if ("type" in data) {
		switch (data.type) {
			case "gauth":
				return data.auth;
			case "backup_code":
				return data.auth.join(", ");
			default:
				return "";
		}
	}

	return "";
};

type AuthUnion = GAuthSchema | IMAPSchema | BackupCodeSchema | CookieAuth;

interface FieldDef<T extends AuthUnion> {
	label: string;
	render: (auth: T) => React.ReactNode;
}

export function getAuthFields(
	auth: AuthUnion | undefined,
	t: TranslationF,
): FieldDef<AuthUnion>[] {
	if (!auth?.type) return [];

	const config: {
		cookie: FieldDef<CookieAuth>[];
		gauth: FieldDef<GAuthSchema>[];
		backup_code: FieldDef<BackupCodeSchema>[];
		imap: FieldDef<IMAPSchema>[];
	} = {
		cookie: [{ label: t("auth.cookie"), render: (_a) => "" }],
		gauth: [{ label: t("auth.gauth"), render: (a) => a.auth }],
		backup_code: [
			{ label: t("auth.backupCode"), render: (a) => a.auth.join(", ") },
		],
		imap: [
			{ label: t("auth.imapServer"), render: (a) => a.server },
			{ label: t("auth.imapPort"), render: (a) => a.port },
			{ label: t("auth.imapUsername"), render: (a) => a.username },
			{ label: t("auth.imapPassword"), render: (a) => a.password },
			{ label: t("auth.imapSsl"), render: (a) => String(a.ssl) },
		],
	};

	switch (auth.type) {
		case "cookie":
			return config.cookie as FieldDef<AuthUnion>[];
		case "gauth":
			return config.gauth as FieldDef<AuthUnion>[];
		case "backup_code":
			return config.backup_code as FieldDef<AuthUnion>[];
		case "imap":
			return config.imap as FieldDef<AuthUnion>[];
		default: {
			const _exhaustive: never = auth;
			throwOnDev(`Unknown auth type: ${JSON.stringify(_exhaustive)}.`);
			return [];
		}
	}
}

export const parseBooleanField = (value: any): boolean | string => {
	if (typeof value === "boolean") return value;
	if (value === null || value === undefined) return false;

	const str = String(value).toLowerCase().trim();
	if (str === "true" || str === "1") return true;
	if (str === "false" || str === "0") return false;

	return String(value);
};
